import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import l18n from '../../../../shared/strings';

import { findCollection } from '../../../../store/helpers/collections';
import { activeCollectionSelector } from '../../../../store/selectors/collections';
import { selectedAssetsIdsSelector } from '../../../../store/selectors/assets';
import { getChildren, addCollection } from '../../../../store/actions/collections';
import { addToCollection } from '../../../../store/actions/assets';

import { showDialog } from '../../../../components/dialog';
import { navigate } from '../../../history';
import UiBlocker from '../../../../services/UiBlocker';
import Logger from '../../../../services/Logger';
import ua from '../../../../ua';
import picsioConfig from '../../../../../../../config';
import renderWebsitePresetsDialog from '../../../../components/WebsitePresetsDialog/renderWebsitePresetsDialog';
import showSlectSharingMethodDialog from '../../../showSelectSharingMethodDialog';
import * as utils from '../../../../shared/utils';

const isRoot = (path) => path === 'root';

export default function useShareAssets(initiation_location = 'unknown') {
  const websitePresets = useSelector((state) => state.websiteSettings.presets);
  const selectedAssetsIds = useSelector(selectedAssetsIdsSelector);

  const collections = useSelector((store) => store.collections.collections);
  const rootCollection = collections?.my;
  const activeCollections = useSelector(activeCollectionSelector);

  const activeCollectionWithCreatePermission = useMemo(() => {
    if (activeCollections.length) return activeCollections.find((activeCollection) => activeCollection?.permissions?.createCollections);
    return rootCollection?.permissions?.createCollections ? rootCollection : undefined;
  }, [activeCollections, rootCollection]);

  const dispatch = useDispatch();

  const share = async () => {
    if (selectedAssetsIds.length === 1) {
      /** Navigate to single sharing page */
      navigate(`/singlesharing/${selectedAssetsIds[0]}`);
      return;
    }

    const result = await showSlectSharingMethodDialog({ initiation_location, assetIds: selectedAssetsIds }, selectedAssetsIds.length > picsioConfig.bulkShareAsSasAssetsLimit);

    if (!result) {
      /** Cancelled */
      return;
    }

    if (result === 'sas') {
      /** Navigate to bulk sharing page */
      navigate('/singlesharing/bulk');
      return;
    }

    /** Create collection and website */
    let parentCollection = findCollection(collections, 'my', { _id: activeCollectionWithCreatePermission?._id });
    let newCollectionName;
    if (parentCollection?.hasChild && !parentCollection?.nodes) {
      await dispatch(getChildren(activeCollectionWithCreatePermission));
      parentCollection = findCollection(collections, 'my', { _id: activeCollectionWithCreatePermission?._id });
    }
    const newCollectionPath = isRoot(parentCollection?.path)
      ? '/'
      : `${parentCollection?.path}${parentCollection?.name}`;

    try {
      const deprecatedCollectionNames = (parentCollection?.nodes || [])
        .map(({ name }) => name.toLowerCase());
      Logger.log('UI', 'NameSharedCollectionModalShown', { assetIds: selectedAssetsIds });
      const { input } = await new Promise((resolve, reject) => {
        const {
          title, textBtnOk, text, placeholder, label,
        } = l18n.WEBSITES.shareAssetsDialog;
        showDialog({
          title,
          textBtnOk,
          text,
          input: {
            value: '',
            label,
            placeholder,
            validate: (value) => {
              const newValue = value.trim().toLowerCase();
              if (newValue === '') {
                return true;
              }
              return newValue && !deprecatedCollectionNames.includes(newValue);
            },
          },
          disableOk: (state) => {
            if (state.input === '') {
              return true;
            }
            return state?.errors?.input;
          },
          onCancel: reject,
          onOk: resolve,
        });
      });
      newCollectionName = input;
      Logger.log(
        'User',
        'NameSharedCollectionModalContinueClicked',
        { assetIds: selectedAssetsIds, collection_name: newCollectionName },
      );
    } catch (error) {
      /** Cancelled */
      Logger.log('User', 'NameSharedCollectionModalClosed');
      return;
    }

    UiBlocker.block(l18n.DETAILS.textProcessing);

    try {
      /** Create collection */
      const newCollection = await dispatch(
        addCollection(newCollectionPath, newCollectionName, parentCollection._id),
      );
      const collectionPath = newCollection.path.substring(1) + newCollection.name;

      /** Attach assets */
      await dispatch(addToCollection({
        collectionID: newCollection._id,
        collectionPath,
        assetIDs: selectedAssetsIds,
        skipCheckMultipleChanges: true,
      }));

      /** Create website */
      const hideWebsitePresetsDialog = utils.LocalStorage.get('picsio.hideWebsitePresetsDialog');
      const isNotDesktop = ua.browser.isNotDesktop();

      if (hideWebsitePresetsDialog || isNotDesktop) {
        Logger.log('User', 'CollectionInfoWebsiteSettings', { collectionId: newCollection._id });
        navigate(`/websites/${newCollection._id}?tab=main`);
      } else {
        renderWebsitePresetsDialog({ collectionId: newCollection._id, websitePresets });
      }
    } catch (error) {
      Logger.error(new Error('Can not create website in one click'), { error }, [
        'OneClickCreateWebsiteFailed',
        { errorMessage: (error && error.message) || 'NoMessage' },
      ]);
    } finally {
      UiBlocker.unblock();
    }
  };

  return { share };
}
